/* eslint-disable react/display-name */
import React, { useState, useEffect } from 'react';

import { useTable, useSortBy, usePagination, useGlobalFilter, useRowSelect } from 'react-table';

import axios from "axios";
import { isEmpty } from "underscore";

import { Pagination } from "../util/tables/Pagination";
import { SearchableTable } from "../util/tables/SearchableTable";
import { IndeterminateCheckbox } from "../util/tables/IndeterminateCheckbox.jsx";

const ConsistencyTable = ({ columns, data, t, selectAllUnknownTitle, reload, setReload, setLoading, setError }) => {
    const [ reallyDelete, setReallyDelete ] = useState(false);
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page, // Instead of using 'rows', we'll use page,
        // which has only the rows for the active page

        // The rest of these things are super handy, too ;)
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        toggleRowSelected,
        setPageSize,
        selectedFlatRows,
        state,
        visibleColumns,
        setGlobalFilter,
    } = useTable({
        columns,
        data,
        initialState: {
            selectedRowIds: [],
            pageSize: 20,
            sortBy: [{id: 'name', desc: true}],
        },
        autoResetGlobalFilter: false,
        autoResetSortBy: false,
        autoResetSelectedRows: false,
    }, useGlobalFilter, useSortBy, usePagination, useRowSelect,
    hooks => {
        hooks.visibleColumns.push(columns => [
        // Let's make a column for selection
            {
                id: 'selection',
                // The header can use the table's getToggleAllRowsSelectedProps method
                // to render a checkbox
                Header: () => (
                    <div>
                        {t("groups.header_remove")}
                    </div>
                ),
                // The cell can use the individual row's getToggleRowSelectedProps method
                // to the render a checkbox
                Cell: ({ row }) => (
                    <div>
                        {(row?.original?.childUuid?.indexOf('sensor/') === 0 || row?.original?.groupMemberName === undefined) && <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} /> }
                    </div>
                ),
            },
            ...columns,
        ]);
    });

    const { selectedRowIds } = state;

    const selectAllUnknown = () => {
        for (const idx in data) {
            const row = data[idx];
            if (row.memberState === 'gone' || row.memberState === 'invalid_inlet' || row.memberState === undefined) {
                toggleRowSelected("" + idx, true);
            }
        }
    };

    const selectAllUnknownButton = <button className="btn btn-primary" onClick={selectAllUnknown}>{ selectAllUnknownTitle }</button>;

    const checkIsInletPort = /^sensor\/[^/]+\/i\/[^/]+$/;
    const doDelete = () => {
        if (!reallyDelete) {
            setReallyDelete(true);
        } else {
            setLoading(true);
            const childrenToRemove = selectedFlatRows.filter(d => (
                d.original.childUuid.indexOf('sensor/') === 0 ||
                d.original.groupMemberName === undefined)
            ).map(d => (
                d.original.childUuid.indexOf('sensor/') === 0
                    && !checkIsInletPort.test(d.original.childUuid)
                    ? d.original.childUuid + "/%" : d.original.childUuid));
            console.log("deleting", childrenToRemove);
            axios.post('/apiv2/groups/clean/remove', childrenToRemove).then(() =>  {
                // Invalidate parent
                setReload(reload + 1);
            }, error => {
                setError(error);
                console.warn("Cannot delete", error);
            });
            
        }
    };
    const reallyRemoveButton = 
        <button className={"btn " + (reallyDelete ? "btn-error" : "btn-outline-error")} disabled={isEmpty(selectedRowIds)} onClick={doDelete}>
            {t("groups.button_" + (reallyDelete ? "really_remove__" : "remove__"), { count: Object.keys(selectedRowIds).length })}
        </button>;

    return (
        <React.Fragment>
            { selectAllUnknownButton } { reallyRemoveButton }
            <SearchableTable tableProps={getTableProps()} globalFilter={state.globalFilter} setGlobalFilter={setGlobalFilter}
                             state={ state }  columns={columns} visibleColumns={visibleColumns} headerGroups={headerGroups}
                             prepareRow={ prepareRow } getTableBodyProps={ getTableBodyProps } page={ page } />
            <Pagination page={ page } pageCount={ pageCount } pageOptions={ pageOptions }
                canPreviousPage={ canPreviousPage } canNextPage={ canNextPage } gotoPage={ gotoPage }
                previousPage={ previousPage } nextPage={ nextPage } setPageSize={ setPageSize }
                pageSize={ state.pageSize } pageIndex={ state.pageIndex } />
        </React.Fragment>
    );
};

const ConsistencyPage = ({p, t }) => {
    const [ groupCtrl, setGroupCtrl ] = useState(false);
    const [ data, setData ] = useState([]);
    const [ selectAllUnknownTitle, setSelectAllUnknownTitle ] = useState(null);

    const [ subscription, setSubscription ] = useState(null);
    const [ reload, setReload ] = useState(0);
    const [ loading, setLoading ] = useState(true);
    
    const [ error, setError ] = useState(null);
    const columns = [
        {
            Header: t("groups.dc_item"),
            accessor: 'groupName',
        }, {
            accessor: 'groupMemberName',
            Header: t("groups.header_missing_child"),
        }, {
            accessor: 'memberStateTranslated',
            Header: t("groups.header_missing_state"),
        }
    ];

    useEffect(() => {
        p.subscribe("group/*", setGroupCtrl, true).then((sub) => {
            setSubscription(sub);
        }, (error) => {
            setError({error});
        });
        
        return function cleanup() {
            if (subscription) {
                subscription();
            }
        };
    }, []);

    useEffect(() => {
        // Only get once we're subscribed to DS
        if (subscription === null) {
            return;
        }
        setLoading(true);
        const url = '/apiv2/groups/clean/fetch';
        axios.get(url).then(response => {
            const rows = [];
            let removeCount = 0;
            for (const groupId in response.data) {
                const degradedChildren = response.data[groupId];
                const groupNode = p.getPath('group/' + groupId);

                for (const childUuid in degradedChildren) {
                    const childNode = p.getPath(childUuid);
                    const state = degradedChildren[childUuid]?.toLowerCase();
                    if (state === undefined || state === 'gone' || state === 'invalid_inlet') {
                        removeCount++;
                    }
                    const translatedState = state ? t("groups.state." + state, t("connectionState." + state)) : t("groups.state.gone");

                    rows.push({
                        groupId,
                        groupName: groupNode?.name,
                        childUuid,
                        groupMemberName: childNode?.name,
                        memberState: state,
                        memberStateTranslated: translatedState,
                        isSelected: false,
                    });
                }
            }
            setData(rows);
            setLoading(false);
            setSelectAllUnknownTitle(t("groups.button_remove___gone", { count: removeCount}));
        }, error => {
            setError(error);
            setLoading(false);
        });
    }, [subscription, reload]);

    if (loading) {
        return  (
            <div className="loader-element">
                <div className="loading loading-lg"></div>
            </div>
        );
    }
    if (error) {
        return <div className="empty">
            <div className="empty-icon">
                <i className="icon icon-flag"></i>
            </div>
            <p className="empty-title h5"><strong>{ t("general.Error") }</strong></p>
            <p className="empty-subtitle">{ t("reporting.error_loading") }</p>
        </div>;
    }
    if (isEmpty(data)) {
        return (
            <div className="empty">
                <div className="empty-icon">
                    <i className="icon icon-check"></i>
                </div>
                <p className="empty-title h5">{ t("general.no_errors") }</p>
                <p className="empty-subtitle">{ t("groups.title_all_items_ok") }</p>
            </div>
        );
    }
    return <div className="item">
        <div className="hero hero-sm">
            <div className="hero-body">
                <h1>{t("groups.title_consistency")}</h1>
                <p>{t("groups.subtitle_consistency1")}</p>
                <p>{t("groups.subtitle_consistency2")}</p>
            </div>
        </div>
        <ConsistencyTable t={t} p={p} data={data} columns={columns} selectAllUnknownTitle={selectAllUnknownTitle}
            setReload={setReload} reload={reload} setLoading={setLoading} setError={setError} />
    </div>;
};

export default ConsistencyPage;